import SharedPaymentComponent from 'aa/vue/components/payment/SharedPaymentComponent';
import SummaryFTC from 'aa/vue/components/SummaryFTC';
import { PAGE_TYPES as AA_PAGE_TYPES } from 'aa/vue/constants/aaTrackingConstants';
import Domain from 'helpers/Domain';
import { AA_PACKAGE_SWITCH } from 'helpers/featureConstants';
import Step from 'models/steps/Step';

import {
    CAMPAIGN_SERVICE_ENABLED,
    SINGLE_COLUMN_PAYMENT_PAGE,
    SKIP_EXPLAINER_STEPS,
} from 'aa/helpers/featureConstants';
import {
    displayFeedbackToUser,
    handleProcessedPayment,
    handleProcessedPaymentError,
    hasValidStoredPaymentMethod,
    redirectLinkToSummary,
} from 'aa/vue/components/payment/signupUtil';
import { getSelectedPlanFromSession } from 'aa/vue/routers/AccountRouter.helpers';

export default class Payment extends Step {
    path = '/account/signup/submitpayment/';
    name = 'PAYMENT';
    requireAuth = true;
    requireNonAuth = false;
    countStep = true;
    props = {
        isEditPayment: false,
        waitingOverlayEnabled: true,
        displayFeedbackToUser,
        handleProcessedPayment,
        handleProcessedPaymentError,
    };

    constructor() {
        const serverData = JSON.parse(document.getElementById('app-config').innerHTML);
        const isFTCComplianceEnabled = serverData.featureFlag?.ftc_compliance_enabled;
        // START TEST PPIWEBM-2873: Single Column Payment INTL
        // PPIWEBM-3017: FF overrides the AB test — when active, show single column regardless of variant.
        const intlSingleColumnPayment =
            !!serverData.featureFlag?.[SINGLE_COLUMN_PAYMENT_PAGE] ||
            serverData.variant?.web_intl_single_column_payment === 'single_column';
        // END TEST PPIWEBM-2873: Single Column Payment INTL

        super();
        this.component =
            isFTCComplianceEnabled || intlSingleColumnPayment ? SummaryFTC : SharedPaymentComponent;

        const enhancedPaymentSummaryEnabled = serverData?.enhancedPaymentSummaryEnabled;
        if (enhancedPaymentSummaryEnabled || isFTCComplianceEnabled || intlSingleColumnPayment) {
            this.name = 'PAYMENT_SUMMARY';
        }
    }

    async beforeEnter(to, from, next) {
        // Important for if user backs out of paying for one plan and chooses another
        this._store.commit('setAutoPromoChecked', false);

        this.getPromo(from);

        if (CBS.Registry.Features[CAMPAIGN_SERVICE_ENABLED]) {
            this._store.commit('setCampaignOfferCoupon');
        }

        if (this._store.getters['flow/skipPayment'] || this._store.getters['user/isSubscriber']) {
            window.location.href = this._store.getters['flow/destinationUrl'];
            return;
        }

        const isExSubscriber = this._store.getters['user/isExSubscriber'];
        // START TEST PPIWEBM-2873: Single Column Payment INTL
        // PPIWEBM-3017: FF overrides the AB test — when active, show single column regardless of variant.
        const intlSingleColumnPayment =
            !!CBS.Registry.Features[SINGLE_COLUMN_PAYMENT_PAGE] ||
            !!this._store.getters['variant/isSingleColumnPaymentVariant'];
        // END TEST PPIWEBM-2873: Single Column Payment INTL
        const paymentExsubBillingSummary = this._store.state.serverData.paymentExsubBillingSummary;
        if (intlSingleColumnPayment && isExSubscriber && paymentExsubBillingSummary) {
            await hasValidStoredPaymentMethod(this._store);
        }

        return await super.beforeEnter(to, from, next);
    }

    /**
     * @returns {{path: *}|null}
     */
    async checkFlowRedirect(from = null) {
        if (
            Domain.isDomestic() &&
            this._store.getters.featureIsActive(AA_PACKAGE_SWITCH) &&
            !this._store.getters['flow/skipPlan']
        ) {
            const selectedPlan = getSelectedPlanFromSession();

            if (!selectedPlan) {
                if (this._store.getters.featureIsActive(SKIP_EXPLAINER_STEPS)) {
                    return { name: 'PLAN' };
                }
                return { name: 'plan_explainer' };
            }

            this._store.dispatch('plan/updateSelectedPlan', selectedPlan);
        }

        if (this._store.getters['flow/isEdu']) {
            if (this._store.getters['user/eduCoupon']) {
                this._store.commit('setPromo', this._store.getters['user/eduCoupon']);
            } else {
                return {
                    path: this._store.getters['flow/getFirstStepForAuth'].path,
                };
            }
        } else if (this._store.getters['flow/isGift'] && !this._store.state.user.isLoggedIn) {
            return {
                path: { name: 'SIGNUP' },
            };
        }
        const summaryRedirect = await redirectLinkToSummary(this._store);

        if (summaryRedirect) {
            return summaryRedirect;
        }

        return null;
    }

    /**
     * @param to
     * @returns {{path: *, name: *}}
     */
    getTrackingArgs(to) {
        const isExSubscriber = this._store.getters['user/isExSubscriber'];
        const selectedPlan = this._store.getters['plan/getSelectedPlan'];
        const productParams = this._store.getters['getPaymentPageProductParamsForTracking'];

        return {
            ...super.getTrackingArgs(to),
            pageTitle: 'Paramount+ Payment Information - ParamountPlus.com',
            pageType: isExSubscriber ? AA_PAGE_TYPES.EXSUB_PAYMENT : AA_PAGE_TYPES.PAYMENT,
            pageName: this.path,
            userType: this._store.state.user.svod.status,
            purchaseProduct: selectedPlan?.recurlyCode,
            purchaseQuantity: '1',
            purchaseCategory: selectedPlan?.purchaseCategory,
            purchasePrice: this._store.getters.bundleShowtime
                ? productParams.showtimeBundleRawPrice()
                : selectedPlan.rawPrice,
            pickPlanType: selectedPlan?.planTier,
            pickPlanSku: selectedPlan?.recurlyCode,
            purchaseEventBillingStart: '1',
            productOfferPeriod:
                selectedPlan?.purchaseOfferPeriod || productParams.productOfferPeriod,
            productPricingPlan: selectedPlan?.planType || productParams.productPricingPlan,
            purchaseProductName: selectedPlan?.titleForTracking,
            productNewSku: selectedPlan.recurlyCode,
            pageUrl: window.location.href,
            purchasePromoCode: this._store.state.promo,
        };
    }
}
